// DeviceIcon Types
// Shared type definitions for the device icon system

export type DeviceIconType = 
  | 'light' 
  | 'switch' 
  | 'outlet' 
  | 'power_meter' 
  | 'power_meter_3phase'
  | 'hvac' 
  | 'fan' 
  | 'th_sensor' 
  | 'presence_sensor'
  | 'motion_sensor'
  | 'door_sensor' 
  | 'curtain' 
  | 'blind' 
  | 'dehumidifier'
  | 'button' 
  | 'controller' 
  | 'bridge' 
  | 'multi_sensor'
  | 'lux_sensor'
  | 'erv';

export interface DeviceIconProps {
  /** Whether the device is ON (affects icon appearance) */
  isOn?: boolean;
  /** Icon size: 'small' (20px), 'medium' (32px), 'large' (40px), or custom number */
  size?: 'small' | 'medium' | 'large' | number;
  /** Additional CSS classes */
  className?: string;
  /** Whether to show animations (e.g., fan spinning, presence pulse) */
  animated?: boolean;
}

/** Convert size prop to pixel value */
export function getSizeInPixels(size: DeviceIconProps['size']): number {
  if (typeof size === 'number') return size;
  switch (size) {
    case 'small': return 20;
    case 'medium': return 32;
    case 'large': return 40;
    default: return 20;
  }
}

/** Common colors for device states */
export const DeviceIconColors = {
  // ON state colors by device type
  light: { on: 'text-yellow-500', off: 'text-slate-400' },
  switch: { on: 'text-green-500', off: 'text-slate-400' },
  outlet: { on: 'text-green-500', off: 'text-slate-400' },
  power_meter: { on: 'text-blue-500', off: 'text-blue-500' },
  power_meter_3phase: { on: 'text-blue-500', off: 'text-blue-500' },
  hvac: { on: 'text-cyan-500', off: 'text-slate-400' },
  fan: { on: 'text-cyan-500', off: 'text-slate-400' },
  th_sensor: { on: 'text-orange-500', off: 'text-orange-500' },
  presence_sensor: { occupied: 'text-red-500', unoccupied: 'text-green-500' },
  motion_sensor: { occupied: 'text-red-500', unoccupied: 'text-green-500' },
  door_sensor: { open: 'text-red-500', closed: 'text-green-500' },
  curtain: { on: 'text-teal-500', off: 'text-slate-400' },
  blind: { on: 'text-cyan-500', off: 'text-slate-400' },
  dehumidifier: { on: 'text-blue-500', off: 'text-slate-400' },
  button: { on: 'text-blue-500', off: 'text-blue-500' },
  controller: { on: 'text-purple-500', off: 'text-purple-500' },
  bridge: { on: 'text-purple-500', off: 'text-purple-500' },
  multi_sensor: { on: 'text-indigo-500', off: 'text-indigo-500' },
  lux_sensor: { on: 'text-amber-500', off: 'text-amber-500' },
  erv: { on: 'text-teal-500', off: 'text-slate-400' },
} as const;
